import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Sparkles, Clock, CheckCircle2, XCircle, Loader2, X, ChevronRight,
  TrendingUp, BookOpen, Briefcase, Palette, Cpu, Users, Trash2, RefreshCw, Zap,
  Ban, Share2, Play
} from "lucide-react";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_ICONS = {
  market: TrendingUp,
  academic: BookOpen,
  business: Briefcase,
  creative: Palette,
  technical: Cpu,
};

const LOCALE_MAP = {
  tr: "tr-TR", en: "en-US", de: "de-DE", fr: "fr-FR", es: "es-ES",
  ar: "ar", ru: "ru-RU", zh: "zh-CN", ja: "ja-JP",
};

/** Deep Research jobs history list. Click a row to open its detail view. */
export const DeepResearchJobsPanel = ({ open, onOpenChange, onSelectJob, user }) => {
  const { t, language } = useLanguage();
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/deep-research/jobs`)
      .then((r) => setJobs(r.data.jobs || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    load();
  }, [open]);

  const openJob = (jobId) => {
    onOpenChange(false);
    setTimeout(() => onSelectJob?.(jobId), 250);
  };

  const deleteJob = async (jobId, e) => {
    e.stopPropagation();
    if (!confirm(t("dr_job_delete_confirm"))) return;
    setBusy(jobId);
    try {
      await axios.delete(`${API}/deep-research/jobs/${jobId}`);
      toast.success(t("dr_job_deleted"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dr_error"));
    } finally {
      setBusy(null);
    }
  };

  const rerunJob = async (jobId, e, mode = "same") => {
    e.stopPropagation();
    setBusy(jobId);
    try {
      const res = await axios.post(`${API}/deep-research/jobs/${jobId}/rerun`, { mode });
      toast.success(mode === "challenger" ? t("dr_challenger_started") : t("dr_job_rerun_started"));
      onOpenChange(false);
      setTimeout(() => onSelectJob?.(res.data.job_id), 250);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dr_error"));
    } finally {
      setBusy(null);
    }
  };

  const cancelJob = async (jobId, e) => {
    e.stopPropagation();
    if (!confirm(t("dr_job_cancel_confirm"))) return;
    setBusy(jobId);
    try {
      await axios.post(`${API}/deep-research/jobs/${jobId}/cancel`);
      toast.success(t("dr_job_cancelled"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dr_error"));
    } finally {
      setBusy(null);
    }
  };

  const resumeJob = async (jobId, e) => {
    e.stopPropagation();
    setBusy(jobId);
    try {
      await axios.post(`${API}/deep-research/jobs/${jobId}/resume`);
      toast.success(t("dr_job_resumed"));
      onOpenChange(false);
      setTimeout(() => onSelectJob?.(jobId), 250);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dr_error"));
    } finally {
      setBusy(null);
    }
  };

  const shareJob = async (job, e) => {    e.stopPropagation();
    setBusy(job.id);
    try {
      const res = await axios.get(`${API}/deep-research/jobs/${job.id}/report.md`, { responseType: "text" });
      const text = typeof res.data === "string" ? res.data : String(res.data || "");
      const title = `KaanLab · ${(job.task || "").slice(0, 60)}`;
      if (navigator.share) {
        await navigator.share({ title, text: text.slice(0, 6000) });
        toast.success(t("dr_shared_ok"));
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(t("dr_shared_copied"));
      }
    } catch (err) {
      if (err?.name === "AbortError") return; // user dismissed the native share sheet
      toast.error(t("dr_share_failed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="dr-jobs-panel"
        className="max-w-2xl max-h-[85vh] overflow-y-auto bg-gradient-to-br from-zinc-900 via-zinc-900 to-purple-950/30 border-purple-500/30 p-0"
      >
        <button
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-zinc-800/60 hover:bg-zinc-700 text-zinc-300"
        >
          <X size={16} />
        </button>

        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{t("dr_jobs_history_title")}</h2>
              <p className="text-xs text-zinc-400">{t("dr_jobs_history_desc")}</p>
            </div>
            {jobs && (
              <span className="ml-auto text-xs text-purple-300 font-mono">
                {jobs.length} {t("dr_jobs_total")}
              </span>
            )}
          </div>
        </div>

        <div className="p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-purple-400" />
            </div>
          )}
          {!loading && jobs?.length === 0 && (
            <div className="text-center py-16 text-zinc-500">
              <Sparkles size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">{t("dr_no_jobs_yet")}</p>
              <p className="text-xs mt-1 text-zinc-600">{t("dr_no_jobs_hint")}</p>
            </div>
          )}
          {!loading && jobs?.length > 0 && (
            <div className="space-y-2">
              {jobs.map((j) => (
                <JobRow
                  key={j.id}
                  job={j}
                  t={t}
                  onOpen={() => openJob(j.id)}
                  onDelete={(e) => deleteJob(j.id, e)}
                  onRerun={(e) => rerunJob(j.id, e, "same")}
                  onChallenger={(e) => rerunJob(j.id, e, "challenger")}
                  onCancel={(e) => cancelJob(j.id, e)}
                  onShare={(e) => shareJob(j, e)}
                  onResume={(e) => resumeJob(j.id, e)}
                  locale={language}
                  busy={busy === j.id}
                  isAdmin={user?.is_admin}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const JobRow = ({ job, t, onOpen, onDelete, onRerun, onChallenger, onCancel, onShare, onResume, locale, busy, isAdmin }) => {
  const Icon = DOMAIN_ICONS[job.domain] || Sparkles;
  const status = job.status || "queued";
  const isDone = status === "completed";
  const isFailed = status === "failed";
  const isRunning = status === "running";
  const isActive = isRunning || status === "queued";
  const isResumable = status === "cancelled" || status === "failed";
  const isTerminal = isDone || isFailed || status === "cancelled";

  const statusIcon = isDone ? (
    <CheckCircle2 size={14} className="text-green-400" />
  ) : isFailed ? (
    <XCircle size={14} className="text-red-400" />
  ) : isRunning ? (
    <Loader2 size={14} className="text-purple-400 animate-spin" />
  ) : (
    <Clock size={14} className="text-zinc-500" />
  );

  const date = job.created_at ? new Date(job.created_at).toLocaleString(LOCALE_MAP[locale] || undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
  }) : "";

  return (
    <div
      data-testid={`dr-job-row-${job.id.slice(0, 8)}`}
      className="w-full flex items-center gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-800/30 hover:bg-zinc-800/60 hover:border-purple-500/40 transition-all group"
    >
      <button onClick={onOpen} className="flex items-center gap-3 flex-1 min-w-0 text-left">
        <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0">
          <Icon size={16} className="text-purple-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white line-clamp-1 group-hover:text-purple-200 transition-colors">
            {job.task}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-zinc-500">
            {statusIcon}
            <span>{t(`dr_${status}`)}</span>
            <span className="opacity-40">•</span>
            <span className="flex items-center gap-1">
              <Users size={11} /> {job.total_agents || 0}
            </span>
            <span className="opacity-40">•</span>
            <span>{date}</span>
          </div>
        </div>
      </button>
      <div className="flex items-center gap-1 flex-shrink-0">
        {isResumable && (
          <button
            data-testid={`dr-job-resume-${job.id.slice(0, 8)}`}
            onClick={onResume}
            disabled={busy}
            title={t("dr_job_resume")}
            className="p-1.5 rounded text-green-500 hover:text-green-400 hover:bg-green-500/10 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          </button>
        )}
        {isActive && (
          <button
            data-testid={`dr-job-cancel-${job.id.slice(0, 8)}`}
            onClick={onCancel}
            disabled={busy}
            title={t("dr_job_cancel")}
            className="p-1.5 rounded text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
          </button>
        )}
        {isDone && (
          <button
            data-testid={`dr-job-share-${job.id.slice(0, 8)}`}
            onClick={onShare}
            disabled={busy}
            title={t("dr_share")}
            className="p-1.5 rounded text-zinc-500 hover:text-sky-400 hover:bg-sky-500/10 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
          </button>
        )}
        {isAdmin && isTerminal && (
          <button
            data-testid={`dr-job-rerun-${job.id.slice(0, 8)}`}
            onClick={onRerun}
            disabled={busy}
            title={t("dr_job_rerun")}
            className="p-1.5 rounded text-zinc-500 hover:text-purple-400 hover:bg-purple-500/10 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        )}
        {isTerminal && (
          <button
            data-testid={`dr-job-delete-${job.id.slice(0, 8)}`}
            onClick={onDelete}
            disabled={busy}
            title={t("delete")}
            className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        )}
        <ChevronRight size={16} className="text-zinc-600 group-hover:text-purple-400 transition-colors" />
      </div>
    </div>
  );
};

export default DeepResearchJobsPanel;
